import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';

import {AreOrganizationSchemasCreatableQuery} from './__generated/AreOrganizationSchemasCreatableQuery';

export const ARE_ORGANIZATION_SCHEMAS_CREATABLE_QUERY: DocumentNode = gql`
  query AreOrganizationSchemasCreatableQuery @skipAudit @engComponent(name: PERMISSIONS) {
    currentUser {
      ... on UserNode {
        id
        organizations {
          admin
        }
        teams {
          isSchemaAdmin
        }
      }
    }
  }
`;

export interface SuccessfulAreOrganizationSchemasCreatableResult {
  queryResultType: QueryResultType.SUCCESS;
  areOrganizationSchemasCreatable: boolean;
}
export interface NonSuccessfulAreOrganizationSchemasCreatableResult {
  queryResultType: Exclude<QueryResultType, QueryResultType.SUCCESS>;
}
export type AreOrganizationSchemasCreatableResult =
  | SuccessfulAreOrganizationSchemasCreatableResult
  | NonSuccessfulAreOrganizationSchemasCreatableResult;

/**
 * A hook to access a GraphQL query fetching if the current user can create organization schemas.
 *
 * After the request is successfully complete, the GraphQL data is transformed to boolean, since it is used to
 * decide if we need to show the create button.
 */
export default function useAreOrganizationSchemasCreatableQuery(): AreOrganizationSchemasCreatableResult {
  const request = useBenchlingQuery<AreOrganizationSchemasCreatableQuery>(
    ARE_ORGANIZATION_SCHEMAS_CREATABLE_QUERY,
    {fetchPolicy: 'cache-and-network'}
  );

  if (request.type !== QueryResultType.SUCCESS) {
    return {queryResultType: request.type};
  }

  if (request.data.currentUser?.__typename !== 'UserNode') {
    return {queryResultType: QueryResultType.SUCCESS, areOrganizationSchemasCreatable: false};
  }

  const areOrganizationSchemasCreatable =
    request.data.currentUser?.organizations?.some((organization) => organization.admin) ||
    request.data.currentUser?.teams?.some((team) => team.isSchemaAdmin);

  return {queryResultType: request.type, areOrganizationSchemasCreatable};
}
